import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { authService } from '@/services/authService';
import { useTranslations } from '@/hooks/useTranslations';

interface IAuthGuardProps {
  children: ReactNode;
  tenantId?: string;
}

/**
 * AuthGuard component that ensures users are authenticated before accessing the app.
 * Automatically redirects unauthenticated users to login.
 */
export function AuthGuard({ children, tenantId }: IAuthGuardProps): ReactElement {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { t } = useTranslations();
  const loginAttemptedRef = useRef(false);

  useEffect(() => {
    let isCancelled = false;

    async function ensureAuthenticated(): Promise<void> {
      if (
        isAuthenticated ||
        inProgress !== InteractionStatus.None ||
        loginAttemptedRef.current
      ) {
        return;
      }

      loginAttemptedRef.current = true;

      try {
        const hydratedInstance = await authService.createAuthProvider(tenantId);

        if (isCancelled) {
          return;
        }

        if (hydratedInstance.getAllAccounts().length > 0) {
          return;
        }

        // Clear the switch_account query param if present (from account switching flow)
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('action')) {
          urlParams.delete('action');
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }

        await authService.login();
      } catch (error) {
        console.error(error);
      }
    }

    void ensureAuthenticated();

    return (): void => {
      isCancelled = true;
    };
  }, [isAuthenticated, inProgress]);

  // Show loading state while checking authentication, redirecting, or handling login
  if (!isAuthenticated || inProgress !== InteractionStatus.None) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem'
      }}>
        {t("app.loadingAuthentication")}
      </div>
    );
  }

  // User is authenticated, render the protected content
  return children as ReactElement;
}
